import _, {find, findLast, first, isUndefined, last} from 'lodash';
import React, {Children, Component, createRef, isValidElement, Key, ReactNode, RefObject} from 'react';
import styled from 'styled-components';

import {
  findScrollParent,
  getWindow,
  isHtmlElement,
} from '../../../../libs/shared/helpers/src/browser/domHelpers';
import {TaskSourcesEnum} from '../../../../libs/shared/helpers/src/instrumentation/eventLoopPerformanceHelpers';
import {
  buildInteractionIdProps,
  InteractionComponentProps,
} from '../../../../libs/shared/helpers/src/interaction/interactionHelpers';
import {EventHandler} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {isShallowEqual} from '../../../../libs/shared/helpers/src/utilities/objectHelpers';
import {WithSizeProps} from '../../../../libs/shared/ui/src/hooks/sizeHooks';
import {FocusModesEnum, ScrollDirectionsEnum} from '../../../../srcCommon/scroll/scrollerTypes';
import {VariableHeightContext, VariableHeightContextProps} from './variableHeightContext';
import {FixedSizesProps, InfiniteScrollerProps} from './variableHeightScrollerTypes';

/*
 * Props.
 */

type VisibleItemsChangeHandler = (computeVisibleItems: () => ReadonlyArray<Key>) => void;

interface VariableHeightScrollerProps extends WithSizeProps, InteractionComponentProps {
  // Keep scroll.
  keepScrollDirection?: ScrollDirectionsEnum;

  // Initial focus.
  focusKey?: Key;
  focusMode?: FocusModesEnum;
  focusMargin?: number;

  // Ensure visible.
  ensureVisibleKey?: Key;

  // Changing this makes the scroller re-focus even if the focusKey didn't change.
  jumpKey?: Key;

  // Infinite scroll.
  infinite?: InfiniteScrollerProps;

  // Performance optimization for known content.
  sizes?: FixedSizesProps;

  // Events.
  onVisibleItemsChange?: VisibleItemsChangeHandler;
  onScroll?: EventHandler;

  // Standard props.
  children: ReactNode;
  className?: string;
}

/*
 * Style.
 */

const StyledScrollDiv = styled.div`
  overflow: auto;
`;

/*
 * Component.
 */

interface ScrollerChildState {
  index: number;
  key: Key;
  ref: RefObject<HTMLDivElement>;
}

type ChildStateMap = {[key: string]: ScrollerChildState | undefined};
type ChildStateList = ReadonlyArray<ScrollerChildState>;

interface VariableHeightScrollerState {
  childStateMap: ChildStateMap;
  childCount: number;
  firstChildState?: ScrollerChildState;
  firstCommonChildState?: ScrollerChildState;
  lastCommonChildState?: ScrollerChildState;
  lastChildState?: ScrollerChildState;
}

interface VariableHeightScrollerSnapshot {
  elementRects: VariableHeightElementRects | undefined;
}

interface SimplifiedRect {
  height: number;
  top: number;
  bottom: number;
}

interface VariableHeightElementRects {
  scrollParentRect: SimplifiedRect;
  scrollElementRect: SimplifiedRect;
  firstCommonChildRect: SimplifiedRect | undefined;
}

interface UpdateScrollRects extends StayAtBottomRects, KeepScrollRects, CompensateResizeRects {
  /** Layout before the current render. */
  snapshotElementRects: VariableHeightElementRects;
}

interface StayAtBottomRects {
  /** Layout before the current render, or undefined if this is the first render. */
  snapshotElementRects: VariableHeightElementRects | undefined;
}

interface KeepScrollRects {
  /** Layout before the current render. */
  snapshotElementRects: VariableHeightElementRects;
  /** Layout after the current render. */
  elementRects: VariableHeightElementRects;
}

interface CompensateResizeRects {
  /** Layout after the last render. */
  previousElementRects: VariableHeightElementRects;
  /** Layout after the current render. */
  elementRects: VariableHeightElementRects;
}

/** The maximum amount of content hidden at the bottom to consider ourselves "scrolled to the bottom." */
const isAtBottomThreshold = 10;

export class VariableHeightScrollerBase extends Component<
  VariableHeightScrollerProps,
  VariableHeightScrollerState,
  VariableHeightScrollerSnapshot
> {
  constructor(props: VariableHeightScrollerProps) {
    super(props);
    this.state = {
      childStateMap: {},
      childCount: 0,
    };

    this.scrollElementRef = createRef();
    this.contextValue = {
      onHeightChange: this.onChildResize,
      onElementLoaded: this.onChildLoaded,
    };
    this.lastScrollTop = 0;
    this.hasInteracted = false;
  }

  private readonly scrollElementRef: RefObject<HTMLDivElement>;
  private readonly contextValue: VariableHeightContextProps;

  private lastScrollTop: number;
  private hasInteracted: boolean;
  private elementRects?: VariableHeightElementRects;

  /*
   * Lifecycle.
   */

  /* eslint no-param-reassign: 0 */
  static getDerivedStateFromProps(
    nextProps: Readonly<VariableHeightScrollerProps>,
    previousState: Readonly<VariableHeightScrollerState>,
  ): VariableHeightScrollerState {
    // Find the matching states for our children. Create new states when needed.
    const childStateList: ChildStateList =
      Children.map(nextProps.children, (c, index) => {
        if (!isValidElement(c) || !c.key) {
          throw new Error('All children need a key.');
        }

        const previousChildState = previousState.childStateMap[c.key];
        if (previousChildState && previousChildState.index === index) {
          return previousChildState;
        }

        if (previousChildState) {
          return {...previousChildState, index};
        }

        return {
          index,
          key: c.key,
          ref: createRef<HTMLDivElement>(),
        };
      }) || [];

    // Create a map of Key to Child State.
    const childStateMap = childStateList.reduce<ChildStateMap>((result, c) => {
      result[c.key] = c;
      return result;
    }, {});

    // Find the last child state that already has an existing ref (common to previous and new states).
    const firstChildState = first(childStateList);
    const firstCommonChildState = find(childStateList, (c) => Boolean(c && c.ref.current));
    const lastCommonChildState = findLast(childStateList, (c) => Boolean(c && c.ref.current));
    const lastChildState = last(childStateList);

    // Return the new state.
    return {
      childStateMap,
      childCount: childStateList.length,
      firstChildState,
      firstCommonChildState,
      lastCommonChildState,
      lastChildState,
    };
  }

  getSnapshotBeforeUpdate(): VariableHeightScrollerSnapshot | null {
    const elementRects = this.computeElementRects();
    return {elementRects};
  }

  componentDidMount() {
    this.elementRects = this.computeElementRects();

    const window = getWindow(this.props.wrapperRef.current);
    if (!window) {
      return;
    }

    // We may need to focus an element after the first render.
    if (!this.maybeFocusElement()) {
      // Otherwise, if this is a bottom-aligned scroller, start at the bottom.
      this.maybeStayAtBottom(undefined, {snapshotElementRects: undefined});
    }

    // Check if we should load more at the top/bottom.
    this.maybeLoadMore();
  }

  componentDidUpdate(
    previousProps: Readonly<VariableHeightScrollerProps>,
    previousState: Readonly<VariableHeightScrollerState>,
    snapshot?: VariableHeightScrollerSnapshot,
  ) {
    const window = getWindow(this.props.wrapperRef.current);
    const previousElementRects = this.elementRects;
    const snapshotElementRects = snapshot?.elementRects;
    const elementRects = this.computeElementRects();
    this.elementRects = elementRects;
    if (!window || !previousElementRects || !snapshotElementRects || !elementRects) {
      return;
    }

    // If needed, update the scroll offset.
    this.maybeUpdateScroll(previousProps, previousState, {
      previousElementRects,
      snapshotElementRects,
      elementRects,
    });

    // If the focus key changed, focus again.
    const {focusKey, ensureVisibleKey, jumpKey} = this.props;
    const shouldJumpForProps = focusKey !== previousProps.focusKey && focusKey !== ensureVisibleKey;
    if (shouldJumpForProps || jumpKey !== previousProps.jumpKey) {
      this.maybeFocusElement();
    }

    // Check if we should load more at the top/bottom.
    this.maybeLoadMore();

    window.requestInstrumentedAnimationFrame(
      () => {
        // If the number of children has changed, report on visible children.
        if (this.state.childCount !== previousState.childCount) {
          this.maybeUpdateVisibleChildren();
        }
      },
      {source: TaskSourcesEnum.COMPONENT_RENDER},
    );
  }

  private maybeUpdateScroll(
    previousProps: Readonly<VariableHeightScrollerProps>,
    previousState: Readonly<VariableHeightScrollerState>,
    rects: UpdateScrollRects,
  ) {
    // If we just need to keep bottom, nothing else to do.
    if (this.maybeStayAtBottom(previousState.childStateMap, rects)) {
      return;
    }

    // Check if there's an element that we need to keep into view.
    if (this.maybeEnsureVisible(previousProps.ensureVisibleKey)) {
      return;
    }

    // Check if we need to compensate for new items at the top.
    if (this.maybeKeepScroll(previousProps, rects)) {
      return;
    }

    // Otherwise, we may need to adapt the scroll after a resize.
    this.maybeCompensateResize(previousState.childStateMap, rects);
  }

  /** If needed, focus a specific element in the list. */
  private maybeFocusElement() {
    if (isUndefined(this.props.focusKey)) {
      return false;
    }

    // Otherwise, find the state for this element.
    const focusChildState = this.state.childStateMap[this.props.focusKey];
    if (!focusChildState) {
      return false;
    }

    // Find the positions of the child and scroll parent.
    const focusChildRect = focusChildState && this.findChildClientRect(focusChildState);
    const scrollParentRect = this.findScrollParentClientRect();
    if (!focusChildRect || !scrollParentRect) {
      return false;
    }

    // Find the desired position based on the focus mode.
    const {focusMode, focusMargin} = this.props;
    const desiredTop = findFocusTop(focusChildRect, scrollParentRect, focusMode, focusMargin);

    // Compensate the scroll accordingly.
    const scrollOffset = Math.ceil(focusChildRect.top - desiredTop);
    this.offsetScroll(scrollOffset);

    return true;
  }

  /** If the last child was at the bottom, adjust the scroll to keep it that way. */
  private maybeStayAtBottom(
    previousChildStateMap: ChildStateMap | undefined,
    {snapshotElementRects}: StayAtBottomRects,
  ) {
    // This only applies to bottom-aligned scrollers.
    if (this.props.keepScrollDirection !== ScrollDirectionsEnum.BOTTOM) {
      return false;
    }

    // No need to do anything if we're still expecting more items.
    const {infinite} = this.props;
    if (infinite && infinite.hasMoreBottom) {
      return false;
    }

    // If this is the first render, assume we were at the bottom to start out.
    const wasAtBottom = !snapshotElementRects || computeIsAtBottom(snapshotElementRects);
    // No need to do anything if the last child wasn't at the bottom.
    if (!wasAtBottom) {
      return false;
    }

    // No need to do anything if nothing changed at the bottom.
    if (isShallowEqual(this.state.childStateMap, previousChildStateMap)) {
      return false;
    }

    const scrollElementRect = this.findScrollElementClientRect();
    const scrollParentRect = this.findScrollParentClientRect();
    if (!scrollElementRect || !scrollParentRect) {
      return false;
    }

    // Fix the scroll and return.
    const scrollOffset = scrollElementRect.bottom - scrollParentRect.bottom;
    this.offsetScroll(Math.ceil(scrollOffset));

    return true;
  }

  /** Ensure that the specified key is visible in the viewport. */
  private maybeEnsureVisible(previousEnsureVisibleKey?: Key) {
    // If there's no key, or if it hasn't changed, nothing to do.
    const {ensureVisibleKey} = this.props;
    if (!ensureVisibleKey || ensureVisibleKey === previousEnsureVisibleKey) {
      return false;
    }

    // Otherwise, find the state for this element.
    const ensureVisibleChildState = this.state.childStateMap[ensureVisibleKey];
    if (!ensureVisibleChildState) {
      return false;
    }

    // Get the positions of parent and child.
    const ensureVisibleChildRect = this.findChildClientRect(ensureVisibleChildState);
    const scrollParentRect = this.findScrollParentClientRect();

    if (!ensureVisibleChildRect || !scrollParentRect) {
      return false;
    }

    // If the desired position hasn't changed, nothing to do.
    const desiredTop = findEnsureVisibleTop(ensureVisibleChildRect, scrollParentRect);
    if (desiredTop === ensureVisibleChildRect.top) {
      return false;
    }

    // Otherwise, compensate the scroll.
    const scrollOffset = Math.ceil(ensureVisibleChildRect.top - desiredTop);
    this.offsetScroll(scrollOffset);

    return true;
  }

  /** Keep the scroll alongside the specified direction. */
  private maybeKeepScroll(
    previousProps: VariableHeightScrollerProps,
    {snapshotElementRects, elementRects}: KeepScrollRects,
  ) {
    const {infinite} = previousProps;
    const wasLoadingMoreTop = Boolean(infinite && infinite.isLoadingMoreTop);
    if (!wasLoadingMoreTop && this.props.keepScrollDirection !== ScrollDirectionsEnum.BOTTOM) {
      return false;
    }

    // If the first child and first common child are the same, nothing to do.
    const {firstChildState, firstCommonChildState} = this.state;
    if (firstChildState === firstCommonChildState) {
      return false;
    }

    // Get the position for the first common child before and after the current render.
    const snapshotFirstCommonChildRect = snapshotElementRects.firstCommonChildRect;
    const {firstCommonChildRect} = elementRects;
    if (!snapshotFirstCommonChildRect || !firstCommonChildRect) {
      return false;
    }

    // Fix the scroll and return.
    const scrollOffset = Math.ceil(firstCommonChildRect.top - snapshotFirstCommonChildRect.top);
    this.offsetScroll(scrollOffset);

    return true;
  }

  /** Keep the scroll after a resize. */
  private maybeCompensateResize(
    previousChildStateMap: ChildStateMap,
    {previousElementRects, elementRects}: CompensateResizeRects,
  ) {
    // Compensation is only needed if we're trying to keep a bottom scroll.
    if (this.props.keepScrollDirection !== ScrollDirectionsEnum.BOTTOM) {
      return false;
    }

    // Find how much we need to compensate for.
    const {scrollParentRect} = elementRects;

    // If the container grew/shrank from the bottom, such as from the window resizing or a docked composer
    // appearing, scroll the opposite amount to stay anchored to the bottom. (If the container grew/shrank
    // from the top, such as from P3 topbar resizing, allow the content to be pushed down.)
    const parentDelta =
      scrollParentRect.bottom !== previousElementRects.scrollParentRect.bottom
        ? scrollParentRect.height - previousElementRects.scrollParentRect.height
        : 0;
    const parentOffset = -parentDelta;

    const offset = Math.ceil(parentOffset);
    if (offset === 0) {
      return false;
    }

    // Compensate within the bottom buffer.
    this.offsetScroll(offset);
    return true;
  }

  /** Measure the content element and its parent. */
  private computeElementRects(): VariableHeightElementRects | undefined {
    const scrollElementRect = this.findScrollElementClientRect();
    const scrollParentRect = this.findScrollParentClientRect();

    const {firstCommonChildState} = this.state;
    const firstCommonChildRect = firstCommonChildState && this.findChildClientRect(firstCommonChildState);

    if (!scrollElementRect || !scrollParentRect || firstCommonChildRect === null) {
      return undefined;
    }

    return {
      scrollElementRect,
      scrollParentRect,
      firstCommonChildRect,
    };
  }

  /*
   * Scroll logic.
   */

  private readonly onInteraction = () => {
    this.hasInteracted = true;
  };

  private readonly onScroll: EventHandler = (event) => {
    const currentScrollParent = this.props.wrapperRef.current;
    if (!currentScrollParent) {
      return;
    }

    if (this.props.onScroll) {
      this.props.onScroll(event);
    }

    this.lastScrollTop = currentScrollParent.scrollTop;
    this.maybeLoadMore();

    if (this.state.childStateMap) {
      this.maybeUpdateVisibleChildren();
    }
  };

  private offsetScroll(offset: number) {
    // Nothing to do if the offset is 0.
    if (offset === 0) {
      return;
    }

    // Find the ref for the scroll parent + the rects.
    const currentScrollParent = this.props.wrapperRef.current;
    const elementRects = this.computeElementRects();
    if (!currentScrollParent || !elementRects) {
      return;
    }

    // If we're all the way to the bottom, the browser may have already scrolled for us.
    // We limit this to the case where the offset is less than the size of the viewport.
    const {scrollElementRect, scrollParentRect} = elementRects;
    const bottomBuffer = scrollElementRect.height - currentScrollParent.scrollTop - scrollParentRect.height;
    if (Math.floor(bottomBuffer) <= 0 && offset < 0 && offset > -scrollParentRect.height) {
      return;
    }

    // Scroll by the specified offset.
    const newScrollTop = currentScrollParent.scrollTop + offset;
    currentScrollParent.scrollTop = newScrollTop;
    this.lastScrollTop = newScrollTop;
  }

  /*
   * Infinite scroll.
   */

  private maybeLoadMore() {
    // Check if we support infinite scroll behavior.
    const {infinite} = this.props;
    if (!infinite) {
      return;
    }

    const scrollParentRect = this.findScrollParentClientRect();
    const scrollElementRect = this.findScrollElementClientRect();
    if (!scrollParentRect || !scrollElementRect) {
      return;
    }

    // Check if we need to load more data.
    if (infinite.hasMoreTop) {
      this.maybeLoadMoreInDirection(scrollParentRect, scrollElementRect, ScrollDirectionsEnum.TOP);
    }

    if (infinite.hasMoreBottom) {
      this.maybeLoadMoreInDirection(scrollParentRect, scrollElementRect, ScrollDirectionsEnum.BOTTOM);
    }
  }

  private maybeLoadMoreInDirection(
    scrollParentRect: SimplifiedRect,
    scrollElementRect: SimplifiedRect,
    direction: ScrollDirectionsEnum,
  ) {
    // Check if we support infinite scroll behavior.
    const {infinite} = this.props;
    if (!infinite) {
      return;
    }

    // If the remaining space is below the threshold, nothing to do.
    const remainingHeight = computeRemainingScrollHeight(scrollParentRect, scrollElementRect, direction);
    if (remainingHeight > infinite.loadMoreThreshold) {
      return;
    }

    // Otherwise, ask for more content.
    infinite.loadMore(direction);
  }

  /*
   * Measures.
   */

  private findScrollParentClientRect(): SimplifiedRect | null {
    const {sizes, size} = this.props;
    const currentScrollParent = this.props.wrapperRef.current;
    if (!currentScrollParent) {
      return null;
    }

    // Variable mode.
    if (!sizes) {
      return currentScrollParent.getBoundingClientRect();
    }

    // Fixed mode.
    const height = (size && size.height) || currentScrollParent.getBoundingClientRect().height;
    return {height, top: 0, bottom: height};
  }

  private findScrollElementClientRect(): SimplifiedRect | null {
    const {infinite, sizes} = this.props;

    // Variable mode.
    if (!sizes) {
      const currentScrollElement = this.scrollElementRef.current;
      return currentScrollElement && currentScrollElement.getBoundingClientRect();
    }

    // Fixed mode.
    const topLoaderHeight = infinite && infinite.hasMoreTop ? sizes.loaderHeight : 0;
    const bottomLoaderHeight = infinite && infinite.hasMoreBottom ? sizes.loaderHeight : 0;
    const childrenHeight = this.state.childCount * sizes.childHeight;

    const height = topLoaderHeight + childrenHeight + bottomLoaderHeight;
    const top = -this.lastScrollTop;
    const bottom = top + height;

    return {height, top, bottom};
  }

  private findChildClientRect(childState: ScrollerChildState): SimplifiedRect | null {
    const {infinite, sizes} = this.props;

    // Variable mode.
    if (!sizes) {
      const child = childState.ref.current;
      return child && child.getBoundingClientRect();
    }

    // Fixed mode.
    const topLoaderHeight = infinite && infinite.hasMoreTop ? sizes.loaderHeight : 0;
    const childOffset = childState.index * sizes.childHeight;

    const height = sizes.childHeight;
    const top = -this.lastScrollTop + topLoaderHeight + childOffset;
    const bottom = top + height;

    return {height, top, bottom};
  }

  /*
   * Children resize.
   */

  private readonly onChildResize = (previousHeight: number, newHeight: number) => {
    // Nothing to do for top-aligned scrollers.
    if (this.props.keepScrollDirection !== ScrollDirectionsEnum.BOTTOM) {
      return;
    }

    const heightDifference = Math.ceil(newHeight - previousHeight);

    // If the child shrank or stayed the same, the browser's default behavior is fine.
    if (heightDifference <= 0) {
      return;
    }

    const elementRects = this.computeElementRects();
    if (!elementRects) {
      return;
    }

    // If we weren't "scrolled to the bottom" before the child grew, the browser's default behavior is fine.
    const bottomScrollOffset = computeBottomScrollOffset(elementRects);
    const wasAtBottomBeforeResize = bottomScrollOffset - heightDifference < isAtBottomThreshold;
    if (!wasAtBottomBeforeResize) {
      return;
    }

    // Compensate so we stay "scrolled to the bottom," and so that the child appears to grow upwards.
    this.offsetScroll(heightDifference);
  };

  private readonly onChildLoaded = () => {
    // If there was already some user interaction, nothing to do.
    if (this.hasInteracted) {
      return;
    }

    // Otherwise, repeat what we did on the first render (in componentDidMount).
    if (!this.maybeFocusElement()) {
      this.maybeStayAtBottom(undefined, {snapshotElementRects: undefined});
    }
  };

  /*
   * Children visibility.
   */

  private readonly maybeUpdateVisibleChildren = () => {
    if (!this.props.onVisibleItemsChange) {
      return;
    }

    if (!this.state.childStateMap) {
      return;
    }

    this.props.onVisibleItemsChange(this.computeVisibleItems);
  };

  private readonly computeVisibleItems = () =>
    _(this.state.childStateMap)
      .filter((childState) =>
        Boolean(
          childState &&
            childState.ref &&
            childState.ref.current &&
            isElementInParentScrollBounds(childState.ref.current),
        ),
      )
      .map((childState) => childState && childState.key)
      .compact()
      .value();

  /*
   * Render.
   */

  render() {
    const {className, infinite, children} = this.props;
    const {childStateMap} = this.state;
    return (
      <VariableHeightContext.Provider value={this.contextValue}>
        {/* Scroll parent. */}
        <StyledScrollDiv
          ref={this.props.wrapperRef}
          className={className}
          onMouseDown={this.onInteraction}
          onWheel={this.onInteraction}
          onScroll={this.onScroll}
          {...buildInteractionIdProps(this.props.interactionId)}
        >
          {/* Scroll element. */}
          <div ref={this.scrollElementRef}>
            {/* Top loader. */}
            {infinite && infinite.loaderRenderer(ScrollDirectionsEnum.TOP)}
            {/* Children. */}
            {renderChildren(children, childStateMap)}
            {/* Bottom loader. */}
            {infinite && infinite.loaderRenderer(ScrollDirectionsEnum.BOTTOM)}
          </div>
        </StyledScrollDiv>
      </VariableHeightContext.Provider>
    );
  }
}

/*
 * Helpers.
 */

/** Computes the height of the content that is hidden at the bottom of the scroller. */
function computeBottomScrollOffset(elementRects: VariableHeightElementRects) {
  const {scrollElementRect, scrollParentRect} = elementRects;
  return scrollElementRect.bottom - scrollParentRect.bottom;
}

function computeIsAtBottom(elementRects: VariableHeightElementRects) {
  const bottomScrollOffset = computeBottomScrollOffset(elementRects);
  return bottomScrollOffset < isAtBottomThreshold;
}

/** Compute how much more we can scroll in a given direction. */
function computeRemainingScrollHeight(
  scrollParentRect: SimplifiedRect,
  scrollElementRect: SimplifiedRect,
  direction: ScrollDirectionsEnum,
) {
  switch (direction) {
    case ScrollDirectionsEnum.TOP:
      return scrollParentRect.top - scrollElementRect.top;

    case ScrollDirectionsEnum.BOTTOM:
      return scrollElementRect.bottom - scrollParentRect.bottom;

    default:
      throw new Error('This should not happen.');
  }
}

/** Find the desired position of the child to focus. */
function findFocusTop(
  focusChildRect: SimplifiedRect,
  scrollParentRect: SimplifiedRect,
  focusMode?: FocusModesEnum,
  focusMargin?: number,
) {
  // Find the requested margin.
  const topMargin = focusMargin || 0;

  // If we need to be top aligned, no further logic is needed.
  if (focusMode === FocusModesEnum.TOP) {
    return scrollParentRect.top + topMargin;
  }

  // Otherwise, position the child in the middle.
  const halfChildHeight = focusChildRect.height / 2;
  const halfParentHeight = scrollParentRect.height / 2;

  const desiredTop = halfParentHeight - halfChildHeight;

  // Cap to 0 and return.
  return scrollParentRect.top + Math.max(desiredTop, topMargin);
}

/** Find the desired position of the child to keep visible. */
function findEnsureVisibleTop(ensureVisibleRect: SimplifiedRect, scrollParentRect: SimplifiedRect) {
  // If it's above the parent, move it to the top.
  if (ensureVisibleRect.top < scrollParentRect.top) {
    return scrollParentRect.top;
  }

  // If it's below the parent, move it to the bottom.
  if (ensureVisibleRect.bottom > scrollParentRect.bottom) {
    return scrollParentRect.bottom - ensureVisibleRect.height;
  }

  // If we're already visible, return the current top.
  return ensureVisibleRect.top;
}

/** Add the provided Refs to the children. */
function renderChildren(children: ReactNode, childStateMap: ChildStateMap) {
  return Children.map(children, (child) => {
    if (!isValidElement(child) || !child.key) {
      throw new Error('All children need a key.');
    }

    // Find the state for this child.
    const childState = childStateMap[child.key];
    if (!childState) {
      return null;
    }

    // Wrap the child in a div so that we can get a ref.
    return (
      <div key={child.key} ref={childState.ref}>
        {child}
      </div>
    );
  });
}

// Helper function to create to create an isVisible check on an item in an overflow: scroll container
function isElementInParentScrollBounds(element: HTMLElement) {
  if (!isHtmlElement(element)) {
    return false;
  }

  const container = findScrollParent(element);
  if (!container) {
    return false;
  }

  const containerScrollTop = container.scrollTop;
  const containerBottom = containerScrollTop + container.clientHeight;

  const elementTop = element.offsetTop;
  const elementBottom = elementTop + element.clientHeight;

  // Is the top of the element below the container scrolltop AND the bottom of the element above the container bottom?
  if (elementTop >= containerScrollTop && elementBottom <= containerBottom) {
    return true;
  }

  // Is the element's bottom beyond the container's bottom AND the element's top above the container's bottom?
  if (elementBottom > containerBottom && elementTop < containerBottom) {
    return true;
  }

  return false;
}
